import { IosShare } from "@mui/icons-material";
import { Button, Card, CardContent, Skeleton, Stack, styled } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { useParams } from "react-router";
import { FormSubmission, FormSubmissionProperties, FormSubmissionPropertiesSkeleton } from "src/components";
import { useGetFormSubmission } from "src/http";
import { downloadBlob, exportFormSubmissionToPdf } from "src/utilities";
import { DetailPageLayout } from "./DetailPageLayout";

const StickyCard = styled(Card)`
  position: sticky;
  top: 100px;
`;

interface Params {
  formSubmissionGuid: string;
}

const UserFeedbackDetailsPage = () => {
  const { formSubmissionGuid } = useParams<keyof Params>() as Params;
  const { data, isLoading } = useGetFormSubmission(formSubmissionGuid);
  const [isExporting, setIsExporting] = useState(false);

  async function handleExportClicked() {
    if (data === undefined) {
      return;
    }

    setIsExporting(true);

    const recipient = data.recipientUser!.displayName;
    const period = DateTime.fromISO(data.createdDateTimeUtc, { zone: "utc" })
      .toLocal()
      .toLocaleString(DateTime.DATE_MED);
    const filename = `${data.formName} for ${recipient} - ${period}.pdf`;
    const blob = await exportFormSubmissionToPdf(data);

    downloadBlob(filename, blob);
    setIsExporting(false);
  }

  if (isLoading || data === undefined) {
    return (
      <DetailPageLayout
        title={<Skeleton variant="rectangular" height={24} width={200} />}
        sideBarContent={<FormSubmissionPropertiesSkeleton />}
      >
        <CardContent>
          <Stack spacing={2}>
            {[...Array(2)].map((_, i) => (
              <Stack key={i} spacing={1}>
                <Skeleton variant="rectangular" height={30} />
                {[...Array(2)].map((_, j) => (
                  <Stack key={j} spacing={0.5}>
                    <Skeleton variant="rectangular" height={24} />
                    <Skeleton variant="rectangular" height={72} />
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout
      title={data.formName}
      actions={
        <Button variant="outlined" startIcon={<IosShare />} onClick={handleExportClicked} disabled={isExporting}>
          Export
        </Button>
      }
      sideBarContent={
        <StickyCard>
          <CardContent>
            <FormSubmissionProperties formSubmission={data} hideBackButton />
          </CardContent>
        </StickyCard>
      }
    >
      <CardContent>
        <FormSubmission formSubmission={data} />
      </CardContent>
    </DetailPageLayout>
  );
};

export { UserFeedbackDetailsPage };
